"use client"

import { IconMedal } from "@tabler/icons-react"
import { useTranslations } from "next-intl"

import { ApolloPanel } from "@/components/ui/apollo-panel"
import { Badge } from "@/components/ui/badge"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useFormatCurrency } from "@/hooks/use-currency"
import { cn } from "@/lib/utils"

import { MONEY_METRICS } from "@/lib/targets-types"
import type { LeaderboardResponse } from "@/lib/targets-api"

interface AgentPerformanceTableProps {
  data: LeaderboardResponse | null
  isLoading: boolean
  /** Extra classes merged onto the panel. */
  className?: string
}

/**
 * Per-agent performance table — the tabular sibling of {@link
 * import('./leaderboard-widget').LeaderboardWidget}, for the team-leader /
 * admin dashboards that want columns (current value, % of target, bonus)
 * rather than a compact ranked strip.
 *
 * PRESENTATIONAL ONLY — the caller owns the fetch and the `targets.view` gate.
 * Columns are derived strictly from what `LeaderboardResponse` actually holds:
 * `rank`, `user.full_name`, `current_value`, `percentage`, `tier_achieved`
 * (→ bonus amount) and `bonus_earned`. Monetary metrics are formatted with the
 * clinic currency via {@link MONEY_METRICS}.
 */
export function AgentPerformanceTable({
  data,
  isLoading,
  className,
}: AgentPerformanceTableProps) {
  const t = useTranslations("dashboard")
  const { formatCurrencyNoDecimals } = useFormatCurrency()

  const metric = data?.period?.metric
  const isMoneyMetric = metric !== undefined && MONEY_METRICS.has(metric)

  const formatValue = (value: number) =>
    isMoneyMetric ? formatCurrencyNoDecimals(value) : value.toLocaleString()

  if (isLoading) {
    return (
      <ApolloPanel eyebrow={t("agentPerformance.title")} className={className}>
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="flex items-center gap-3">
              <Skeleton className="h-4 w-6 shrink-0" />
              <Skeleton className="h-4 flex-1" />
              <Skeleton className="h-4 w-16 shrink-0" />
              <Skeleton className="h-4 w-12 shrink-0" />
            </div>
          ))}
        </div>
      </ApolloPanel>
    )
  }

  const rows = data?.leaderboard ?? []

  if (!data?.has_active_period || rows.length === 0) {
    return (
      <ApolloPanel eyebrow={t("agentPerformance.title")} className={className}>
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <div className="mb-3 rounded-full bg-muted p-3">
            <IconMedal className="h-6 w-6 text-muted-foreground" />
          </div>
          <p className="text-sm font-medium text-muted-foreground">
            {t("agentPerformance.empty.title")}
          </p>
          <p className="mt-1 text-xs text-muted-foreground/70">
            {t("agentPerformance.empty.body")}
          </p>
        </div>
      </ApolloPanel>
    )
  }

  return (
    <ApolloPanel
      eyebrow={t("agentPerformance.title")}
      actions={
        data.period ? (
          <Badge variant="outline" className="text-[10px]">
            {data.period.period_label}
          </Badge>
        ) : undefined
      }
      className={className}
    >
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-10">{t("agentPerformance.column.rank")}</TableHead>
            <TableHead>{t("agentPerformance.column.agent")}</TableHead>
            <TableHead className="text-right">
              {data.period?.metric_label ?? t("agentPerformance.column.value")}
            </TableHead>
            <TableHead className="text-right">
              {t("agentPerformance.column.progress")}
            </TableHead>
            <TableHead className="text-right">
              {t("agentPerformance.column.bonus")}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((entry) => (
            <TableRow key={`${entry.rank}-${entry.user.id}`}>
              <TableCell className="tabular-nums text-muted-foreground">
                {entry.rank}
              </TableCell>
              <TableCell className="max-w-[180px] truncate font-medium">
                {entry.user.full_name}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {formatValue(entry.current_value ?? 0)}
              </TableCell>
              <TableCell
                className={cn(
                  "text-right font-semibold tabular-nums",
                  entry.percentage >= 100
                    ? "text-emerald-600 dark:text-emerald-400"
                    : entry.percentage >= 80
                      ? "text-amber-600 dark:text-amber-500"
                      : "",
                )}
              >
                {entry.percentage.toFixed(1)}%
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {entry.bonus_earned != null && entry.bonus_earned > 0 ? (
                  formatCurrencyNoDecimals(entry.bonus_earned)
                ) : entry.tier_achieved ? (
                  <Badge variant="outline" className="text-[10px]">
                    {formatCurrencyNoDecimals(entry.tier_achieved.bonus_amount)}
                  </Badge>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </ApolloPanel>
  )
}
